import { z } from 'zod';
import { Uuid } from './primitives.js';
/**
 * The summary a scan job reports back to the control plane. Raw dumps stay in object storage
 * under the scan prefix (framer-input-spec.md §4); this is only what the app needs to answer
 * "what is in this project" without opening a file.
 */
export const ScanStatus = z.enum(['succeeded', 'failed', 'partial']);
export const ScanPage = z.object({
    id: z.string().min(1),
    /** Framer allows a page without a path (unrouted); it is kept so counts stay honest. */
    path: z.string().nullable(),
    /** Set when the page is a CMS detail template. */
    collectionId: z.string().nullable(),
    draft: z.boolean(),
});
/** Shape of one CMS field, so the hub can say what a collection holds without opening a dump. */
export const ScanField = z.object({
    id: z.string().min(1),
    name: z.string(),
    type: z.string(),
});
export const ScanCollection = z.object({
    id: z.string().min(1),
    name: z.string(),
    slugFieldName: z.string().nullable(),
    fieldCount: z.number().int().nonnegative(),
    itemCount: z.number().int().nonnegative(),
    draftItemCount: z.number().int().nonnegative(),
    /**
     * Field names and types, never item content — the inventory answers "what is in here", and
     * the Content Pack remains the only place the values themselves are handed over.
     */
    fields: z.array(ScanField),
});
export const ScanStyles = z.object({
    colorCount: z.number().int().nonnegative(),
    textStyleCount: z.number().int().nonnegative(),
    fontFamilies: z.array(z.string()),
});
/** A step that failed without sinking the scan — surfaced to the user as `FRAMER_PARTIAL`. */
export const ScanWarning = z.object({
    scope: z.enum(['pages', 'collection', 'styles', 'components', 'publish']),
    /** Collection id or style kind, when the warning is about one thing. */
    target: z.string().nullable(),
    message: z.string().max(500),
});
export const ScanReportRequest = z.object({
    leaseToken: z.string().min(1),
    /** Chosen by the runner so it can upload dumps before the row exists. */
    scanSnapshotId: Uuid,
    status: z.enum(['succeeded', 'partial']),
    /** Must equal `tenants/<userId>/scans/<scanSnapshotId>/`. */
    storagePrefix: z.string().min(1),
    projectName: z.string().nullable(),
    publishUrl: z.string().nullable(),
    pages: z.array(ScanPage),
    collections: z.array(ScanCollection),
    styles: ScanStyles.nullable(),
    warnings: z.array(ScanWarning),
});
export const ScanReportResponse = z.object({
    scanSnapshotId: Uuid,
    status: ScanStatus,
    pageCount: z.number().int().nonnegative(),
    collectionCount: z.number().int().nonnegative(),
});
/**
 * What the project hub shows above the action cards: the last successful scan, summarized.
 * `null` before the first scan, which is the empty state the UI has to render.
 */
export const ProjectInventoryResponse = z.object({
    scan: z
        .object({
        id: Uuid,
        status: ScanStatus,
        scannedAt: z.string().min(1),
        pageCount: z.number().int().nonnegative(),
        collectionCount: z.number().int().nonnegative(),
        publishUrl: z.string().nullable(),
        /** Every page the scan saw, so the hub can show the routes a build would produce. */
        pages: z.array(ScanPage),
        collections: z.array(ScanCollection),
        warnings: z.array(ScanWarning),
    })
        .nullable(),
});
//# sourceMappingURL=scan.js.map